import axios from 'axios';
import { isValidPostCode, isValidEmail } from '../common.js';

const groupBy = (key) => (array) =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});

export default () => {
  return {
    baseUrl: 'https://members-api.parliament.uk/api/Members/',
    scotsbaseURL: 'https://data.parliament.scot/api/',
    members: [],
    emailAddresses: [],
    mSPS: [],

    groupedMembers: [],
    membersInConstituencies: [],
    currentMember: {},
    currentRegion: null,
    regionName: '',
    currentConstituency: null,
    constituencyName: '',
    constituencyMSP: {},
    parties: [],
    partyAffiliation: [],

    open: false,
    message: '',
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    townCity: '',
    postCode: '',
    feedback: '',
    processing: false,
    step: 1,
    openShareModal: false,
    linkCopied: false,

    async init() {
      this.message = this.$refs.message.value;
      this.$watch(
        'firstName, lastName, email, address, townCity, postCode, message',
        () => {
          this.feedback = '';
        },
      );

      this.$watch('open', () => {
        if (this.open) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
      this.$watch('step', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      });

      await this.fetchMembers();
      await this.fetchMembersInConstituencies();
      await this.fetchEmailAddresses();
      await this.fetchParties();
      await this.fetchMembersPartyAffiliations();
    },
    reset() {
      this.members = [];
      this.emailAddresses = [];
      this.mSPS = [];

      this.groupedMembers = [];
      this.membersInConstituencies = [];
      this.currentMember = {};
      this.currentRegion = null;
      this.regionName = '';
      this.currentConstituency = null;
      this.constituencyName = '';
      this.constituencyMSP = {};
      this.parties = [];
      this.partyAffiliation = [];

      this.open = false;
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.address = '';
      this.townCity = '';
      this.postCode = '';
      this.feedback = '';
      this.processing = false;
      this.step = 1;
      this.openShareModal = false;
      this.linkCopied = false;
    },
    dummyData() {
      this.firstName = 'John';
      this.lastName = 'Doe';
      this.email = 'new@thehive.app';
      this.address = '123 Fake Street';
      this.townCity = 'Glasgow';
      this.postCode = 'G1 1AA';
    },
    async findMSPs() {
      this.processing = true;
      if (!isValidPostCode(this.postCode)) {
        this.processing = false;
        this.feedback = 'Please enter a valid postcode';
        this.$refs.search.focus();
        return;
      }

      const getAreas = async () => {
        const url = `https://mapit.mysociety.org/postcode/${encodeURIComponent(this.postCode)}?api_key=k4fQuC4K1lY3Js39Fg1zTIHXfUFheGP2EBbNv32r`;

        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return await response.json();
        } catch (error) {
          return { error };
        }
      };

      const data = await getAreas();

      if (data.error) {
        this.processing = false;
        this.feedback = 'Please enter a valid postcode';
        this.$refs.search.focus();
        return;
      }
      const areas = Object.values(data.areas);

      this.regionName = areas.find(
        (value) => value.type_name === 'Scottish Parliament region',
      )?.name;

      this.constituencyName = areas.find(
        (value) => value.type_name === 'Scottish Parliament constituency',
      )?.name;

      const getRegions = await fetch(`${this.scotsbaseURL}regions`);
      const regions = await getRegions.json();

      if (regions.length === 0) {
        this.feedback = "Couldn't find any MSP for given Post Code";
        this.processing = false;
        return;
      }

      const region = regions.find(
        (region) => region.Name === this.regionName && region.EndDate === null,
      );
      if (region === undefined) {
        this.feedback = "Couldn't find any MSP for given Post Code";
        this.processing = false;
        return;
      }
      this.currentRegion = region.ID;

      this.groupedMembersByRegion();

      const getConstituencies = await fetch(`${this.scotsbaseURL}constituencies`);

      const constituencies = await getConstituencies.json();
      const constituency = constituencies.find(
        (constituency) =>
          constituency.Name === this.constituencyName &&
          constituency.ValidUntilDate === null,
      );

      this.currentConstituency = constituency.ID;

      const getConstituenciesReps = await fetch(
        `${this.scotsbaseURL}MemberElectionConstituencyStatuses`,
      );
      const constituenciesReps = await getConstituenciesReps.json();

      this.constituencyMSP = this.getMemberData(
        constituenciesReps.find(
          (rep) =>
            rep.ConstituencyID === this.currentConstituency &&
            rep.ValidUntilDate === null,
        ).PersonID,
      );

      this.open = true;
      this.processing = false;
    },

    async submit(event) {
      event.preventDefault();
      this.processing = true;
      if (!this.validateForm()) {
        this.processing = false;
        return;
      }

      const formInputEmailAddresses = this.$refs.toEmail.value.split(',');

      const MSPsData = [this.constituencyMSP, ...this.mSPS];

      const form = document.forms.namedItem('_writeToMSPs');
      const payload = new FormData(form);
      const postUrl = form.action;

      const sendMessages = async () => {
        const sendMessage = async (email, msp) => {
          const updatedMessage = `
Dear ${msp.name},

${this.message}

Yours Sincerely,

${this.firstName} ${this.lastName}
`;

          payload.set('message[body]', updatedMessage);
          payload.set('toEmail', email);
          payload.set('receiverName', msp.name);

          try {
            await axios.post(postUrl, payload);
            return 'Your message has been sent successfully.';
          } catch (error) {
            throw new Error(`Something went wrong... ${error.response.data.message}`);
          }
        };

        this.processing = true;
        try {
          const promises = formInputEmailAddresses.map((email, i) =>
            sendMessage(email, MSPsData[i]),
          );
          await Promise.all(promises);

          this.feedback = 'Your message has been sent successfully.';
          this.processing = false;
          this.step = 2;
        } catch (error) {
          this.feedback = error.message;
          this.processing = false;
        } finally {
          this.processing = false;
        }
      };
      await sendMessages();
    },
    validateForm() {
      if (!this.message) {
        this.processing = false;
        this.feedback = 'Please enter your message';
        this.$refs.message.focus();
        return false;
      }
      if (!this.firstName) {
        this.processing = false;
        this.feedback = 'Please enter your first name.';
        this.$refs.firstName.focus();
        return false;
      }
      if (!this.lastName) {
        this.processing = false;
        this.feedback = 'Please enter your last name.';
        this.$refs.lastName.focus();
        return false;
      }
      if (!isValidEmail(this.email)) {
        this.processing = false;
        this.feedback = 'Please enter a valid email address.';
        this.$refs.email.focus();
        return false;
      }
      if (!this.address) {
        this.processing = false;
        this.feedback = 'Please enter your address.';
        this.$refs.address.focus();
        return false;
      }
      if (!this.townCity) {
        this.processing = false;
        this.feedback = 'Please enter your town/city.';
        this.$refs.townCity.focus();
        return false;
      }
      if (!isValidPostCode(this.postCode)) {
        this.processing = false;
        this.feedback = 'Please enter your post code.';
        this.$refs.postCode.focus();
        return false;
      }
      return true;
    },
    async fetchEmailAddresses() {
      const response = await fetch(`${this.scotsbaseURL}emailaddresses`);
      const json = await response.json();
      this.emailAddresses = json;
    },
    async fetchMembers() {
      const response = await fetch(`${this.scotsbaseURL}members`);
      const json = await response.json();
      this.members = json;
    },
    async fetchMembersInConstituencies() {
      const response = await fetch(`${this.scotsbaseURL}MemberElectionregionStatuses`);
      const json = await response.json();
      const items = json.filter((item) => item.ValidUntilDate === null);

      this.membersInConstituencies = items.map((obj) => {
        return { RegionID: obj.RegionID, PersonID: obj.PersonID };
      });
    },
    async fetchParties() {
      const response = await fetch(`${this.scotsbaseURL}Parties`);
      const json = await response.json();
      const items = json.filter((party) => party.ValidUntilDate === null);
      this.parties = items.map((obj) => {
        return {
          PartyID: obj.ID,
          PartyName: obj.ActualName,
        };
      });
    },
    async fetchMembersPartyAffiliations() {
      const response = await fetch(`${this.scotsbaseURL}memberparties`);
      const json = await response.json();
      const items = json.filter((item) => item.ValidUntilDate === null);

      this.partyAffiliation = items.map((obj) => {
        return {
          PersonID: obj.PersonID,
          PartyID: obj.PartyID,
        };
      });
    },

    groupedMembersByRegion() {
      const mspsPerRegion = [];
      const groupByRegion = groupBy('RegionID');
      this.groupedMembers = groupByRegion(this.membersInConstituencies);

      for (const [key, value] of Object.entries(this.groupedMembers)) {
        if (Number.parseInt(key) === this.currentRegion) {
          for (let i = 0; i < value.length; i++) {
            mspsPerRegion.push(value[i].PersonID);
          }
        }
      }

      for (const key of mspsPerRegion) {
        this.mSPS.push(this.getMemberData(key));
      }
    },
    getMemberData(PersonID) {
      const member = this.members.find((member) => member.PersonID === PersonID);

      const name = member.ParliamentaryName.split(', ').reverse().join(' ');
      const photoURL = member.PhotoURL;

      const emailObject = this.emailAddresses.find(
        (email) => email.PersonID === PersonID,
      );

      const email = emailObject.Address;
      const partyObj = this.partyAffiliation.find((party) => party.PersonID === PersonID);

      const party = this.parties.find((partyID) => partyID.PartyID === partyObj.PartyID);

      return {
        name,
        email,
        photoURL,
        party: party.PartyName,
      };
    },
  };
};
